import { Injectable, Renderer2 } from '@angular/core';
import { SprintfPipe } from '../pipes/sprintf.pipe';

interface VisibilityParams {
  selection: number;
  from: number[];
  till: number[];
  opacity: number[];
}

@Injectable()
export class ProcessImageRendererService {
  public sprintf: SprintfPipe;
  public renderer: Renderer2;

  private readonly _formateStrings: { [id: string]: string } = {};
  private readonly _timer: { [elm: string]: any } = {};

  constructor() {}

  public render_VisuVisibility(element: Element, data: number): void {
    if (element) {
      const elementId = element.getAttribute('id');
      const param = this._exParams_VisuVisibility(element);
      const { selection, from, opacity } = param;

      if (selection === 1) {
        if (!from || !opacity) {
          const error = `From or Opacity is missing.`;
          inform(elementId, 'VisuVisibility', error);
          return;
        }
        const fl = from.length;
        const ol = opacity.length;
        if (fl !== ol) {
          const error = `From${fl} and Opacity${ol} dont have the same length.`;
          inform(elementId, 'VisuVisibility', error);
          return;
        }
      }

      this._visuVisibility(element, data, param);
    }
  }

  public render_VisuText(element: Element, data?: number): void {
    const elementId = element.getAttribute('id');
    if (element.hasAttribute('audako-text-signal')) {
      try {
        let formatString;
        if (!this._formateStrings[elementId]) {
          formatString = element.textContent;
          this._formateStrings[elementId] = formatString;
        } else {
          formatString = this._formateStrings[elementId];
        }

        if (formatString) {
          let fmText;
          typeof data !== 'number'
            ? (fmText = this.sprintf.transform(formatString, ''))
            : (fmText = this.sprintf.transform(formatString, data));

          element.children[0].textContent = fmText;
          return;
        }
        inform(elementId, 'VisuText');
      } catch (e) {
        inform(elementId, 'VisuText');
      }
    } else {
      inform(elementId, 'VisuText');
    }
  }

  public render_VisuSlide(element: Element, data: number) {
    if (typeof data !== 'number') {
      return;
    }

    // get the current transform value to add a transform(x , y) on it
    let currentTransform = element.getAttribute('transform');
    // direction is either x or y (vertical or horizontal scroll)
    const direction = element.getAttribute('audako-slide-direction') as 'y' | 'x';
    // min value of the singal
    const min = parseFloat(element.getAttribute('audako-slide-min'));
    // min value of the singal
    const max = parseFloat(element.getAttribute('audako-slide-max'));

    const fadeSpeed = parseFloat(element.getAttribute('audako-slide-speed'));

    if (fadeSpeed) {
      this.renderer.setStyle(element, 'transition', 'transform ' + fadeSpeed + 's');
    }

    // the factor displays the relation between the signal value and the distance the element slides
    // e.g. if the signal has a value of 2 the element should slide 250px factor: 125
    const factor = parseFloat(element.getAttribute('audako-slide-factor'));

    const translated = element.getAttribute('audako-slide-translated');

    let y = 0,
      x = 0;

    if (direction === 'x') {
      x = Math.max(data * factor, min);
      x = Math.min(x, max);
    } else if (direction === 'y') {
      y = Math.max(data * factor, min);
      y = Math.min(y, max);
    }

    if (translated) {
      let newTransform = '';
      const splitted = currentTransform.split(/ /);
      splitted.map((strg, index) => {
        if (index < splitted.length - 1) {
          newTransform += strg + ' ';
        }
      });

      currentTransform = newTransform;
    }

    this.renderer.setAttribute(element, 'transform', currentTransform + ` translate(${x},${y})`);
    this.renderer.setAttribute(element, 'audako-slide-translated', 'true');
  }

  public render_VisuBar(element: Element, data: number): void {
    if (typeof data !== 'number') {
      return;
    }
    if (element) {
      const max = parseFloat(element.getAttribute('audako-bar-max'));
      const height = parseFloat(element.getAttribute('height'));
      const width = parseFloat(element.getAttribute('width'));
      const x = parseFloat(element.getAttribute('x'));
      const y = parseFloat(element.getAttribute('y'));

      let newHeight;
      let newWidth;
      let percentageChange;
      let newX;
      let newY;

      switch (element.getAttribute('audako-bar-direction')) {
        case 'top':
          percentageChange = Math.min(data / max, 1);
          newHeight = percentageChange * height;
          newY = y + (height - newHeight);
          this.renderer.setStyle(element, 'height', newHeight);
          this.renderer.setStyle(element, 'y', newY);
          break;
        case 'bottom':
          percentageChange = Math.min(data / max, 1);
          newHeight = percentageChange * height;
          this.renderer.setStyle(element, 'height', newHeight);
          break;
        case 'left':
          percentageChange = Math.min(data / max, 1);
          newWidth = percentageChange * width;
          newX = x + (width - newWidth);
          this.renderer.setStyle(element, 'width', newWidth);
          this.renderer.setStyle(element, 'x', newX);
          break;
        case 'right':
          percentageChange = Math.min(data / max, 1);
          newWidth = percentageChange * width;
          this.renderer.setStyle(element, 'width', newWidth);
          break;
      }
    }
  }

  public render_VisuBlink(element: Element, data: number): void {
    if (typeof data !== 'number') {
      return;
    }
    if (element) {
      let elementId = element.getAttribute('id');
      if (!elementId) {
        inform(elementId, 'VisuBlink', 'No id');
        return;
      }

      const { blinkFrequency, comparator, compValue } = this._exParams_VisuBlink(element);
      if (
        typeof blinkFrequency !== 'number' ||
        typeof comparator !== 'string' ||
        typeof compValue !== 'number'
      ) {
        const bt = typeof blinkFrequency !== 'number' ? 'blinkFrequency, ' : '';
        const ct = typeof comparator !== 'string' ? 'comparator, ' : '';
        const cvt = typeof compValue !== 'number' ? 'compValue' : '';

        const error = `Parameter(s) missing: ${bt}${ct}${cvt}`;
        inform(elementId, 'VisuBlink', error);
        return;
      }

      if (this._checkCondition(data, comparator, compValue)) {
        if (!element.hasAttribute('started') || element.getAttribute('started') !== 'true') {
          element.setAttribute('started', 'true');
          element.setAttribute('visible', 'true');
          this._timer[elementId] = setInterval(() => {
            this._toggleVisibility(element);
          }, blinkFrequency);
        }
      } else {
        this.renderer.setStyle(element, 'opacity', 0);
        clearInterval(this._timer[elementId]);
        element.setAttribute('started', 'false');
      }
    }
  }

  public render_VisuColor(element: Element, data: any): void {
    if (element.hasAttribute('audako-color-signal')) {
      let formats = {
        from: element.getAttribute('audako-color-from').split(';'),
        till: element.getAttribute('audako-color-till').split(';'),
        colorweb: element.getAttribute('audako-color-colorweb').split(';'),
        strokeColor: element.getAttribute('audako-color-stroke')?.split(';') ?? []
      };

      for (let i = 0; i <= formats.from.length; i++) {
        if (data >= formats.from[i] && data < formats.till[i]) {
          formats.colorweb?.[i] && this.renderer.setStyle(element, 'fill', formats.colorweb[i]);
          formats.strokeColor?.[i] &&
            this.renderer.setStyle(element, 'stroke', formats.strokeColor[i]);
        }
      }
    }
  }

  private _visuVisibility(element: Element, data: number, params: VisibilityParams): void {
    const { selection, from, till, opacity } = params;
    let visibility;

    switch (selection) {
      case 1:
        const fromLength = from.length;
        const tillLength = till.length;

        for (let i = 0; i < fromLength; i++) {
          const f = from[i];
          const t = tillLength > i ? till[i] : undefined;
          //  When till exist, it need to be bigger then value
          if (data >= f && ((t && t > data) || typeof t !== 'number')) {
            visibility = opacity[i];
            break;
          }
        }
        break;
      case 2:
        data === 0 ? (visibility = 1) : (visibility = 0);
        break;
      default:
        data === 0 ? (visibility = 0) : (visibility = 1);
        break;
    }
    if (element.tagName === 'text') {
      this._setVisibility(element.children[0], visibility);
    } else {
      this._setVisibility(element, visibility);
    }
  }

  private _exParams_VisuBlink(element: Element): {
    blinkFrequency: number;
    comparator: string;
    compValue: number;
  } {
    if (element) {
      return {
        blinkFrequency: parseInt(element.getAttribute('audako-blink-frequency'), 10),
        comparator: element.getAttribute('audako-blink-comparator'),
        compValue: parseInt(element.getAttribute('audako-blink-comp_value'), 10)
      };
    } else {
      return undefined;
    }
  }

  private _exParams_VisuVisibility(element: Element): VisibilityParams {
    if (element) {
      let from;
      const rawFrom = element.getAttribute('audako-visibility-from');
      if (rawFrom) {
        from = rawFrom.split(';').map((s) => parseInt(s, 10));
      }

      let till;
      const rawTill = element.getAttribute('audako-visibility-till');
      if (rawTill) {
        till = rawTill.split(';').map((s) => parseInt(s, 10));
      }

      let opacity;
      const rawOpacity = element.getAttribute('audako-visibility-opacity');
      if (rawOpacity) {
        opacity = rawOpacity.split(';').map((s) => parseInt(s, 10));
      }

      return {
        selection: parseInt(element.getAttribute('audako-visibility-selection'), 10),
        from: from,
        till: till,
        opacity: opacity
      };
    } else {
      return undefined;
    }
  }

  private _checkCondition(value: number, comparator: string, comp_value: number) {
    switch (comparator) {
      case '<':
        return value < comp_value;
      case '<=':
        return value <= comp_value;
      case '==':
        return value === comp_value;
      case '>=':
        return value >= comp_value;
      case '>':
        return value > comp_value;
      case '!=':
        return value !== comp_value;
    }
  }

  private _toggleVisibility(element: any) {
    if (!element.visible) {
      this.renderer.setStyle(element, 'opacity', 1);
      element.visible = true;
    } else {
      this.renderer.setStyle(element, 'opacity', 0);
      element.visible = false;
    }
  }

  private _setVisibility(ele: Element, visibility: number) {
    this.renderer.setStyle(ele, 'fill-opacity', visibility);
    this.renderer.setStyle(ele, 'opacity', visibility);
  }
}

export function inform(id: string, animationType: string, error?: string) {
  const type = `Animation Error`;
  const payload = { element: id, animationType: animationType, error: error };
  console.warn({ type, payload });
}
